import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/auth";

const TIERS = [
 { key: null, name: "SPECTATOR", price: "FREE", battles: "Listen & vote only", perks: ["Listen to the whole Charts library", "Watch every battle", "Vote on battles & tracks", "View rankings"] },
 { key: "battle_pass", name: "ARENA + CHARTS PASS", price: "$4.99", battles: "5 battles + unlimited songs", perks: ["5 rap battles / month", "UNLIMITED song drops to Charts", "Tournament eligible", "Full Judge feedback", "All AI opponents"], highlight: true },
 { key: "champion_pass", name: "CHAMPION PASS", price: "$14.99", battles: "20 battles + unlimited songs", perks: ["20 rap battles / month", "UNLIMITED song drops to Charts", "Priority matchmaking", "Champion badge", "Tournament seeded first"] },
];

export function Subscribe() {
 const { user } = useAuth();
 const nav = useNavigate();
 const [busy, setBusy] = useState(null);
 const [err, setErr] = useState("");

 const buy = async (tier_key) => {
 if (!user) {
 // Take the user to sign-in but come STRAIGHT BACK to this page after —
 // no silent redirect that looks like "nothing happened when I tapped".
 nav(`/login?next=${encodeURIComponent("/subscribe")}`);
 return;
 }
 if (!tier_key) return;
 setBusy(tier_key); setErr("");
 try {
 const { data } = await axios.post(`${API}/subscribe/checkout`, {
 tier_key,
 origin_url: window.location.origin,
 });
 window.location.href = data.url;
 } catch (e) {
 setErr(e.response?.data?.detail || "Checkout failed — try again or refresh");
 setBusy(null);
 }
 };

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2"> CHOOSE YOUR PASS</div>
 <h1 className="font-display text-6xl md:text-7xl tracking-tight mb-3">SUBSCRIBE</h1>
 <p className="text-yt-muted max-w-2xl mb-10">One pass, two arenas. Battle live 1-on-1 <span className="text-yt-yellow">AND</span> drop unlimited songs to the Charts. Compete in monthly tournaments, claim your crown, put your name on the leaderboard.</p>

 {err && <div className="font-mono text-xs text-yt-red border border-yt-red/40 bg-yt-red/5 p-3 mb-6">{err}</div>}

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {TIERS.map(t => {
 const isCurrent = (user?.tier === t.key) || (!user && !t.key);
 return (
 <div key={t.name} className={`bg-yt-surface border p-8 ${t.highlight ? "border-yt-yellow shadow-brutal-yellow" : "border-yt-border"}`}>
 <div className="font-mono text-xs tracking-[0.3em] text-yt-muted">{t.name}</div>
 <div className="font-display text-6xl tracking-tight mt-2 mb-1">{t.price}</div>
 {t.price !== "FREE" && <div className="text-yt-muted text-sm">per month</div>}
 <div className="font-mono text-xs text-yt-yellow mt-4">{t.battles}</div>
 <ul className="mt-6 space-y-2 text-sm text-white/80">
 {t.perks.map(p => <li key={p}>· {p}</li>)}
 </ul>
 {isCurrent ? (
 <div className="mt-8 text-center font-mono text-xs tracking-widest text-yt-judge border border-yt-judge py-3">✓ CURRENT PLAN</div>
 ) : t.key ? (
 <button
 data-testid={`buy-${t.key}`}
 disabled={busy === t.key}
 onClick={() => buy(t.key)}
 className="mt-8 w-full bg-yt-yellow text-black font-display text-lg tracking-wider py-3 hover:bg-white shadow-brutal disabled:opacity-50">
 {busy === t.key ? "LOADING..." : !user ? "SIGN IN TO SUBSCRIBE →" : t.name === "CHAMPION PASS" ? "GO CHAMPION " : "GET PASS "}
 </button>
 ) : null}
 </div>
 );
 })}
 </div>
 </div>
 );
}

export function SubscribeSuccess() {
 const [params] = useSearchParams();
 const { refresh } = useAuth();
 const [status, setStatus] = useState("checking");
 const sessionId = params.get("session_id");

 useEffect(() => {
 let attempts = 0;
 const poll = async () => {
 if (!sessionId) { setStatus("error"); return; }
 try {
 const { data } = await axios.get(`${API}/subscribe/status/${sessionId}`);
 if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
 if (data.status === "expired") { setStatus("expired"); return; }
 } catch {}
 if (attempts++ < 8) setTimeout(poll, 2500);
 else setStatus("timeout");
 };
 poll();
 }, [sessionId]);

 return (
 <div className="max-w-2xl mx-auto px-6 py-20 text-center">
 {status === "checking" && <><h1 className="font-display text-5xl">CONFIRMING...</h1><div className="font-mono text-yt-muted mt-4">Verifying payment with Stripe...</div></>}
 {status === "paid" && (
 <>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-judge"> VERDICT</div>
 <h1 className="font-display text-7xl text-yt-yellow mt-2">PASS ACTIVE</h1>
 <div className="font-mono text-yt-muted mt-4">Your battles have been credited. Step in the arena.</div>
 <Link to="/battles/new" data-testid="success-cta" className="inline-block mt-8 bg-yt-yellow text-black font-display text-lg tracking-wider px-8 py-4 shadow-brutal">▶ START BATTLING</Link>
 </>
 )}
 {(status === "expired" || status === "timeout" || status === "error") && (
 <>
 <h1 className="font-display text-5xl text-yt-red">CHECKOUT NOT COMPLETE</h1>
 <Link to="/subscribe" className="inline-block mt-6 underline">Try again</Link>
 </>
 )}
 </div>
 );
}
